import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  message,
  Tooltip,
  Dropdown,
  Menu,
  Progress,
  Avatar,
  Badge,
  Tabs,
  List,
  Drawer,
  Timeline,
  InputNumber,
  Checkbox,
  Divider,
  Collapse,
  Rate,
  Alert
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  PhoneOutlined,
  MailOutlined,
  UserOutlined,
  DollarOutlined,
  CalendarOutlined,
  TrophyOutlined,
  RiseOutlined,
  FallOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  TeamOutlined,
  FunnelPlotOutlined,
  LineChartOutlined,
  ExportOutlined,
  FilterOutlined,
  SearchOutlined,
  StarOutlined,
  PercentageOutlined,
  BankOutlined,
  ProjectOutlined,
  SolutionOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;
const { TextArea } = Input;

interface Opportunity {
  id: string;
  title: string;
  customer: string;
  leadSource: string;
  contactPerson: string;
  email: string;
  phone: string;
  stage: 'qualification' | 'needs-analysis' | 'proposal' | 'negotiation' | 'closed-won' | 'closed-lost';
  probability: number;
  expectedRevenue: number;
  expectedCloseDate: string;
  actualCloseDate?: string;
  salesRep: string;
  territory: string;
  products: string[];
  competitorInfo: string;
  notes: string;
  nextStep: string;
  lostReason?: string;
  createdDate: string;
  lastActivity: string;
  activities: Activity[];
  quotations: Quotation[];
}

interface Activity {
  id: string;
  type: 'call' | 'email' | 'meeting' | 'demo' | 'proposal' | 'follow-up';
  title: string;
  description: string;
  date: string;
  status: 'planned' | 'completed' | 'cancelled';
  outcome?: string;
  createdBy: string;
}

interface Quotation {
  id: string;
  number: string;
  amount: number;
  status: 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired';
  validUntil: string;
  createdDate: string;
}

const OpportunityTracking: React.FC = () => {
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState<Opportunity | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [detailsDrawerVisible, setDetailsDrawerVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [form] = Form.useForm();

  // Mock data
  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      const mockOpportunities: Opportunity[] = [
        {
          id: '1',
          title: 'Enterprise Software Solution - TechCorp',
          customer: 'TechCorp Solutions',
          leadSource: 'Website',
          contactPerson: 'John Smith',
          email: 'john.smith@techcorp.com',
          phone: '+1-555-0123',
          stage: 'proposal',
          probability: 75,
          expectedRevenue: 150000,
          expectedCloseDate: '2024-07-15',
          salesRep: 'Sarah Johnson',
          territory: 'North America',
          products: ['ERP Software', 'Training', 'Support'],
          competitorInfo: 'Competing with Microsoft Dynamics',
          notes: 'Large opportunity, decision makers identified, budget confirmed.',
          nextStep: 'Present final proposal to board',
          createdDate: '2024-06-01',
          lastActivity: '2024-06-24',
          activities: [
            {
              id: 'a1',
              type: 'demo',
              title: 'Product Demo',
              description: 'Demonstrated core features to technical team',
              date: '2024-06-20T14:00:00',
              status: 'completed',
              outcome: 'Very positive feedback, technical requirements approved',
              createdBy: 'Sarah Johnson'
            }
          ],
          quotations: [
            {
              id: 'q1',
              number: 'QUO-2024-001',
              amount: 150000,
              status: 'sent',
              validUntil: '2024-07-20',
              createdDate: '2024-06-25'
            }
          ]
        },
        {
          id: '2',
          title: 'CRM Implementation - StartupCo',
          customer: 'Startup Innovations',
          leadSource: 'LinkedIn',
          contactPerson: 'Emily Davis',
          email: 'emily.davis@startup.io',
          phone: '+1-555-0124',
          stage: 'needs-analysis',
          probability: 60,
          expectedRevenue: 45000,
          expectedCloseDate: '2024-08-30',
          salesRep: 'Mike Chen',
          territory: 'West Coast',
          products: ['CRM Software', 'Implementation'],
          competitorInfo: 'Evaluating Salesforce and HubSpot',
          notes: 'Growing startup, looking for cost-effective solution.',
          nextStep: 'Prepare detailed needs analysis and pricing',
          createdDate: '2024-06-10',
          lastActivity: '2024-06-23',
          activities: [],
          quotations: []
        }
      ];
      setOpportunities(mockOpportunities);
      setLoading(false);
    }, 1000);
  }, []);

  const getStageColor = (stage: string) => {
    const colors = {
      'qualification': 'blue',
      'needs-analysis': 'cyan',
      'proposal': 'orange',
      'negotiation': 'purple',
      'closed-won': 'green',
      'closed-lost': 'red'
    };
    return colors[stage as keyof typeof colors] || 'default';
  };

  const getStageProgress = (stage: string) => {
    const progress = {
      'qualification': 20,
      'needs-analysis': 40,
      'proposal': 60,
      'negotiation': 80,
      'closed-won': 100,
      'closed-lost': 0
    };
    return progress[stage as keyof typeof progress] || 0;
  };

  const handleAddOpportunity = () => {
    setSelectedOpportunity(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditOpportunity = (opportunity: Opportunity) => {
    setSelectedOpportunity(opportunity);
    form.setFieldsValue({
      ...opportunity,
      expectedCloseDate: dayjs(opportunity.expectedCloseDate),
    });
    setModalVisible(true);
  };

  const handleViewDetails = (opportunity: Opportunity) => {
    setSelectedOpportunity(opportunity);
    setDetailsDrawerVisible(true);
  };

  const handleSaveOpportunity = async (values: any) => {
    try {
      const opportunityData = {
        ...values,
        id: selectedOpportunity?.id || Date.now().toString(),
        expectedCloseDate: values.expectedCloseDate.format('YYYY-MM-DD'),
        lastActivity: dayjs().format('YYYY-MM-DD'),
        activities: selectedOpportunity?.activities || [],
        quotations: selectedOpportunity?.quotations || []
      };

      if (selectedOpportunity) {
        setOpportunities(opportunities.map(opp => 
          opp.id === selectedOpportunity.id ? opportunityData : opp
        ));
        message.success('Opportunity updated successfully');
      } else {
        setOpportunities([...opportunities, opportunityData]);
        message.success('Opportunity created successfully');
      }

      setModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error('Failed to save opportunity');
    }
  };

  const columns: ColumnsType<Opportunity> = [
    {
      title: 'Opportunity',
      key: 'opportunity',
      width: 250,
      render: (_, record) => (
        <div>
          <div>
            <Text strong>{record.title}</Text>
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.customer} • {record.contactPerson}
          </div>
          <div style={{ marginTop: 4 }}>
            <Progress 
              percent={getStageProgress(record.stage)} 
              size="small" 
              showInfo={false}
              strokeColor={getStageColor(record.stage)}
            />
          </div>
        </div>
      ),
    },
    {
      title: 'Stage',
      dataIndex: 'stage',
      key: 'stage',
      width: 120,
      render: (stage) => (
        <Tag color={getStageColor(stage)}>
          {stage.replace('-', ' ').toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Expected Revenue',
      dataIndex: 'expectedRevenue',
      key: 'expectedRevenue',
      width: 140,
      render: (value) => (
        <Text strong style={{ color: '#1890ff' }}>
          ${value.toLocaleString()}
        </Text>
      ),
      sorter: (a, b) => a.expectedRevenue - b.expectedRevenue,
    },
    {
      title: 'Probability',
      dataIndex: 'probability',
      key: 'probability',
      width: 120,
      render: (probability) => (
        <div>
          <Progress 
            percent={probability} 
            size="small"
          />
          <Text style={{ fontSize: '12px' }}>{probability}%</Text>
        </div>
      ),
      sorter: (a, b) => a.probability - b.probability,
    },
    {
      title: 'Expected Close',
      dataIndex: 'expectedCloseDate',
      key: 'expectedCloseDate',
      width: 130,
      render: (date) => {
        const isOverdue = dayjs(date).isBefore(dayjs(), 'day');
        return (
          <Text style={{ color: isOverdue ? '#ff4d4f' : 'inherit' }}>
            <CalendarOutlined /> {dayjs(date).format('MMM DD, YYYY')}
          </Text>
        );
      },
    },
    {
      title: 'Sales Rep',
      dataIndex: 'salesRep',
      key: 'salesRep',
      width: 130,
      render: (salesRep) => (
        <Space>
          <Avatar size="small" icon={<UserOutlined />} />
          {salesRep}
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button 
              type="link" 
              icon={<EyeOutlined />} 
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button 
              type="link" 
              icon={<EditOutlined />} 
              onClick={() => handleEditOpportunity(record)}
            />
          </Tooltip>
          <Tooltip title="Call">
            <Button type="link" icon={<PhoneOutlined />} />
          </Tooltip>
          <Tooltip title="Email">
            <Button type="link" icon={<MailOutlined />} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const opportunityStats = {
    total: opportunities.length,
    open: opportunities.filter(o => !o.stage.includes('closed')).length,
    won: opportunities.filter(o => o.stage === 'closed-won').length,
    lost: opportunities.filter(o => o.stage === 'closed-lost').length,
    totalValue: opportunities.reduce((sum, opp) => sum + opp.expectedRevenue, 0),
    weightedValue: opportunities.reduce((sum, opp) => 
      sum + (opp.expectedRevenue * opp.probability / 100), 0
    )
  };

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2}>
            <FunnelPlotOutlined style={{ marginRight: 8 }} />
            Opportunity Tracking
          </Title>
        </Col>
        <Col>
          <Space>
            <Button icon={<ExportOutlined />}>Export</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddOpportunity}>
              Add Opportunity
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Statistics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small">
            <Statistic
              title="Total Opportunities"
              value={opportunityStats.total}
              prefix={<FunnelPlotOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small">
            <Statistic
              title="Open Opportunities"
              value={opportunityStats.open}
              prefix={<ProjectOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small">
            <Statistic
              title="Total Pipeline Value"
              value={opportunityStats.totalValue}
              prefix={<DollarOutlined />}
              formatter={(value) => `$${Number(value).toLocaleString()}`}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small">
            <Statistic
              title="Weighted Pipeline"
              value={opportunityStats.weightedValue}
              prefix={<PercentageOutlined />}
              formatter={(value) => `$${Number(value).toLocaleString()}`}
              valueStyle={{ color: '#13c2c2' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Opportunities Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={opportunities}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1200 }}
          pagination={{
            //pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: [10, 20, 50, 100, 500],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} opportunities`,
          }}
        />
      </Card>

      {/* Add/Edit Opportunity Modal */}
      <Modal
        title={selectedOpportunity ? 'Edit Opportunity' : 'Add New Opportunity'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        width={900}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveOpportunity}
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="title"
                label="Opportunity Title"
                rules={[{ required: true, message: 'Please enter opportunity title' }]}
              >
                <Input placeholder="e.g., Enterprise Software Solution - TechCorp" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="customer"
                label="Customer/Company"
                rules={[{ required: true, message: 'Please enter customer name' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="contactPerson"
                label="Contact Person"
                rules={[{ required: true, message: 'Please enter contact person' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Please enter email' },
                  { type: 'email', message: 'Please enter valid email' }
                ]}
              >
                <Input prefix={<MailOutlined />} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Phone"
                rules={[{ required: true, message: 'Please enter phone number' }]}
              >
                <Input prefix={<PhoneOutlined />} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="stage"
                label="Stage"
                rules={[{ required: true, message: 'Please select stage' }]}
              >
                <Select>
                  <Option value="qualification">Qualification</Option>
                  <Option value="needs-analysis">Needs Analysis</Option>
                  <Option value="proposal">Proposal</Option>
                  <Option value="negotiation">Negotiation</Option>
                  <Option value="closed-won">Closed Won</Option>
                  <Option value="closed-lost">Closed Lost</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="probability"
                label="Probability (%)"
                rules={[{ required: true, message: 'Please enter probability' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  max={100}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="expectedRevenue"
                label="Expected Revenue ($)"
                rules={[{ required: true, message: 'Please enter expected revenue' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="expectedCloseDate"
                label="Expected Close Date"
                rules={[{ required: true, message: 'Please select expected close date' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="salesRep"
                label="Sales Representative"
                rules={[{ required: true, message: 'Please select sales rep' }]}
              >
                <Select>
                  <Option value="Sarah Johnson">Sarah Johnson</Option>
                  <Option value="Mike Chen">Mike Chen</Option>
                  <Option value="Alex Thompson">Alex Thompson</Option>
                  <Option value="Lisa Wang">Lisa Wang</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="territory"
                label="Territory"
                rules={[{ required: true, message: 'Please select territory' }]}
              >
                <Select>
                  <Option value="North America">North America</Option>
                  <Option value="Europe">Europe</Option>
                  <Option value="Asia Pacific">Asia Pacific</Option>
                  <Option value="Latin America">Latin America</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="notes" label="Notes">
            <TextArea rows={3} placeholder="Additional notes about the opportunity..." />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {selectedOpportunity ? 'Update Opportunity' : 'Create Opportunity'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Opportunity Details Drawer */}
      <Drawer
        title={selectedOpportunity?.title || 'Opportunity Details'}
        width={700}
        open={detailsDrawerVisible}
        onClose={() => setDetailsDrawerVisible(false)}
      >
        {selectedOpportunity && (
          <div>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={8}>
                <Card size="small" title="Stage Progress">
                  <Progress
                    type="circle"
                    percent={getStageProgress(selectedOpportunity.stage)}
                    strokeColor={getStageColor(selectedOpportunity.stage)}
                    size={80}
                  />
                  <div style={{ textAlign: 'center', marginTop: 8 }}>
                    <Tag color={getStageColor(selectedOpportunity.stage)}>
                      {selectedOpportunity.stage.replace('-', ' ').toUpperCase()}
                    </Tag>
                  </div>
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small" title="Probability">
                  <Progress
                    type="circle"
                    percent={selectedOpportunity.probability}
                    size={80}
                  />
                  <div style={{ textAlign: 'center', marginTop: 8 }}>
                    <Text strong>{selectedOpportunity.probability}%</Text>
                  </div>
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small" title="Expected Revenue">
                  <div style={{ textAlign: 'center' }}>
                    <Statistic
                      value={selectedOpportunity.expectedRevenue}
                      prefix={<DollarOutlined />}
                      formatter={(value) => Number(value).toLocaleString()}
                      valueStyle={{ fontSize: '24px', color: '#1890ff' }}
                    />
                  </div>
                </Card>
              </Col>
            </Row>

            <Divider orientation="left">Customer Information</Divider>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <p><strong>Customer:</strong> {selectedOpportunity.customer}</p>
                <p><strong>Contact Person:</strong> {selectedOpportunity.contactPerson}</p>
                <p><strong>Email:</strong> {selectedOpportunity.email}</p>
                <p><strong>Phone:</strong> {selectedOpportunity.phone}</p>
              </Col>
              <Col span={12}>
                <p><strong>Lead Source:</strong> {selectedOpportunity.leadSource}</p>
                <p><strong>Sales Rep:</strong> {selectedOpportunity.salesRep}</p>
                <p><strong>Territory:</strong> {selectedOpportunity.territory}</p>
                <p><strong>Expected Close:</strong> {dayjs(selectedOpportunity.expectedCloseDate).format('MMMM DD, YYYY')}</p>
              </Col>
            </Row>

            <Divider orientation="left">Opportunity Details</Divider>
            <div style={{ marginBottom: 16 }}>
              <p><strong>Products/Services:</strong> {selectedOpportunity.products.join(', ')}</p>
              <p><strong>Competitor Info:</strong> {selectedOpportunity.competitorInfo}</p>
              <p><strong>Next Step:</strong> {selectedOpportunity.nextStep}</p>
              <p><strong>Notes:</strong> {selectedOpportunity.notes}</p>
            </div>

            <div style={{ marginTop: 24 }}>
              <Space>
                <Button type="primary" onClick={() => handleEditOpportunity(selectedOpportunity)}>
                  Edit Opportunity
                </Button>
                <Button icon={<PhoneOutlined />}>Call</Button>
                <Button icon={<MailOutlined />}>Email</Button>
                <Button icon={<CalendarOutlined />}>Schedule Meeting</Button>
                <Button icon={<FileTextOutlined />}>Create Quotation</Button>
              </Space>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default OpportunityTracking;
